import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import {
  CapturePointWorkbench,
  WorkbenchSkeleton,
  resumeSampleTour,
  type JobAppLinkRoute,
} from '@paige/capturepoint';
import { useDataSource } from '../api/data-source';
import { usePrincipal } from '../auth/AuthBoundary';
import { isAdmin, runsJobs } from '../tenant/role';
import { jobQuery } from '../api/queries';
import type { JobDetail } from '../api/types';
import { engineOriginFor, isMissing, loadReview } from '../api/route-loaders';
import { useWorkbenchPerson } from '../shell/profile-actions';
import { useReadOnlyView } from '../shell/read-only-view';
import { EngineOpening } from '../screens/review/EngineOpening';
import { useOpenWorkspaceJob } from '../screens/review/open-workspace-job';
import { placeFor, reviewSearchSchema } from '../screens/review/review-search';

/**
 * Level 3: CapturePoint's review workbench over the job's engine (docs/engineering/workbench-port.md).
 * The job names its workspace job; the API's `/v1/jobs/{id}/engine` and `/pages` are the channel.
 *
 * The loader reads the job and warms the engine before the route commits, so the dashboard stays
 * until the workbench has what it needs; when the engine takes longer than a beat the route shows
 * the workbench's own shape, which the workbench then holds until its documents are up. One
 * transition, no empty frame (IMPLEMENTATION.md, sell-ready sprint 2, row 13).
 */
export const Route = createFileRoute('/t/$tenant/jobs/$job/review')({
  validateSearch: reviewSearchSchema,
  loader: ({ context, params }) => loadReview(context, params.tenant, params.job),
  pendingComponent: WorkbenchSkeleton,
  pendingMs: 500,
  component: RouteComponent,
});

function RouteComponent() {
  const { tenant, job } = Route.useParams();
  const { setup, destination, open } = Route.useSearch();
  const navigate = useNavigate();
  const setupShown = useDropSetupRequest();
  const placeLink = useOneShotPlace(job, placeFor(open));
  const user = useWorkbenchUser();
  const person = useWorkbenchPerson(tenant);
  // The job door's other jobs open on their own routes (open-workspace-job.ts).
  const openJob = useOpenWorkspaceJob(tenant);
  const newJob = useNewJob(tenant);
  const access = useWorkbenchAccess(tenant);
  // Warm from the loader; the live stream keeps it current while the workbench is open.
  const query = useQuery(jobQuery(useDataSource(), tenant, job));
  const freshTour = useSampleTour(tenant, query.data, access.runsJobs && !access.readOnly);
  if (query.isPending || freshTour) return <WorkbenchSkeleton />;
  // Deleted while it was open: the 404 page, like its address opened cold.
  if (query.isError) throw isMissing(query.error) ? notFound() : query.error;
  // A job with no workspace job yet is bound on its first engine call, then opens (row 21).
  if (!query.data.workspaceJobId) return <EngineOpening tenant={tenant} jobId={job} />;
  return (
    <CapturePointWorkbench
      jobId={query.data.workspaceJobId}
      mode={query.data.state === 'setup' ? 'setup' : 'capture'}
      openSetup={setup === 'export' ? 'export' : setup === 1}
      openDestination={setup === 'export' ? destination : undefined}
      onSetupShown={setupShown}
      engineOrigin={engineOriginFor(job)}
      tenant={tenant}
      user={user}
      {...access}
      {...placeLink}
      onLeave={() => void navigate({ to: '/t/$tenant', params: { tenant } })}
      onNewJob={newJob}
      onOpenJob={openJob}
      person={person}
    />
  );
}

/**
 * The tour's sample job resumes its tour (owner, 2026-10-06): the job carries the tour's step, and
 * the tour is written back into this browser before the workbench mounts, so any device opens it
 * where the person left off rather than as a job of their own. A tour that never reached its first
 * card has nowhere to resume: true, and the person is taken to a fresh tour, which replaces this job.
 * Decided once per job; a tour already running here keeps its own place.
 */
function useSampleTour(tenant: string, job: JobDetail | undefined, mayTour: boolean): boolean {
  const navigate = useNavigate();
  const workspaceJobId = job?.workspaceJobId ?? null;
  const sample = job?.sampleTour ?? null;
  const isSample = sample !== null;
  const step = sample?.step ?? null;
  const fresh = useMemo(
    () =>
      isSample && workspaceJobId !== null
        ? resumeSampleTour(workspaceJobId, step, mayTour) === 'restart'
        : false,
    // The step the job carried when it was opened; later steps are this browser's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSample, workspaceJobId, mayTour],
  );
  useEffect(() => {
    if (!fresh) return;
    void navigate({
      to: '/t/$tenant/jobs/new',
      params: { tenant },
      search: { tour: 1 },
      replace: true,
    });
  }, [fresh, navigate, tenant]);
  return fresh;
}

/** New job is Paige's new-job route; Start my own job (the tour's last step) opens its name step. */
function useNewJob(tenant: string): (mode?: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (mode?: string) =>
      void navigate({
        to: '/t/$tenant/jobs/new',
        params: { tenant },
        search: mode ? { start: 'name' as const } : {},
      }),
    [navigate, tenant],
  );
}

/**
 * What the person may do in the workbench: change the job's setup, run the company, or only read
 * (Ademero support under a read-only grant reads the documents and changes nothing; round 3, B1).
 */
function useWorkbenchAccess(tenant: string) {
  const principal = usePrincipal();
  const readOnly = useReadOnlyView();
  return useMemo(
    () => ({
      runsJobs: runsJobs(principal, tenant),
      runsCompany: isAdmin(principal, tenant),
      readOnly,
    }),
    [principal, tenant, readOnly],
  );
}

/** The signed-in person, one object per person, so the workbench's bridge is not rebuilt every render. */
function useWorkbenchUser(): { id: string; name: string } {
  const { id, name } = usePrincipal();
  return useMemo(() => ({ id, name }), [id, name]);
}

/**
 * `?open=` (a place in the job, from an email's button; review-search.ts) is a one-shot request too: the
 * place is kept for this job's mount, handed to the workbench as its link, and the address forgets it in
 * place (no history entry), so a reload or a shared link opens the documents. Kept per job: another job
 * opened from here is another route, and it opens on its documents.
 */
function useOneShotPlace(
  job: string,
  asked: JobAppLinkRoute | undefined,
): { appLink?: JobAppLinkRoute } {
  const navigate = useNavigate();
  const [kept, setKept] = useState(() => (asked ? { job, place: asked } : null));
  // A newer address asking for a place (this route opened again for another job, or another place) replaces it.
  if (asked && (kept?.job !== job || kept.place !== asked)) setKept({ job, place: asked });
  useEffect(() => {
    if (!asked) return;
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, open: undefined }),
      replace: true,
    });
  }, [asked, navigate]);
  return kept?.job === job ? { appLink: kept.place } : {};
}

/**
 * `?setup=` (and its `destination`) is a one-shot request: once the workbench shows the setup, the address forgets it in
 * place (no history entry), so a reload or a shared link opens the documents.
 */
function useDropSetupRequest(): () => void {
  const navigate = useNavigate();
  return useCallback(
    () =>
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          setup: undefined,
          destination: undefined,
        }),
        replace: true,
      }),
    [navigate],
  );
}
